import React, { useEffect, useMemo, useState } from "react";
import {
  Avatar, Box, Button, Chip, CircularProgress, Grid, IconButton, InputAdornment, MenuItem, Stack,
  TextField, Typography,
} from "@mui/material";
import SearchIcon from "@mui/icons-material/Search";
import RefreshIcon from "@mui/icons-material/Refresh";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import CancelOutlinedIcon from "@mui/icons-material/CancelOutlined";
import LocationOnOutlinedIcon from "@mui/icons-material/LocationOnOutlined";
import LoginOutlinedIcon from "@mui/icons-material/LoginOutlined";
import LogoutOutlinedIcon from "@mui/icons-material/LogoutOutlined";
import GroupsOutlinedIcon from "@mui/icons-material/GroupsOutlined";
import { toast } from "react-toastify";
import api from "../../api/client";
import { PageHeader, SectionCard, StatCard, StatusChip, EmptyState } from "../ui";
import { extractErrorMessage } from "../../utils/errorMessage";
import { sortByLabel } from "../../utils/sortOptions";

function safeDate(d) {
  if (!d) return null;
  const dt = new Date(d);
  return Number.isNaN(dt.getTime()) ? null : dt;
}

const fmtDate = (d) => {
  const dt = safeDate(d);
  if (!dt) return "—";
  return dt.toLocaleDateString("en-IN", { day: "numeric", month: "short", year: "numeric" });
};

const fmtTime = (d) => {
  const dt = safeDate(d);
  if (!dt) return "—";
  return dt.toLocaleTimeString("en-IN", { hour: "2-digit", minute: "2-digit", hour12: true });
};

const fmtDateTime = (d) => {
  const dt = safeDate(d);
  if (!dt) return "—";
  return dt.toLocaleString("en-IN", {
    day: "numeric",
    month: "short",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
    hour12: true,
  });
};

export default function ManagerAttendanceApprovalsNew() {
  const [requests, setRequests] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState("pending");
  const [search, setSearch] = useState("");
  const [remarks, setRemarks] = useState({});
  const [actingId, setActingId] = useState("");

  const load = async () => {
    setLoading(true);
    try {
      const res = await api.get("/api/attendance/manager-approvals");
      setRequests(res.data?.requests || res.data || []);
    } catch (err) {
      toast.error(extractErrorMessage(err));
    } finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const stats = useMemo(() => {
    const pending = requests.filter((r) => r.status === "pending").length;
    const approved = requests.filter((r) => r.status === "approved").length;
    const rejected = requests.filter((r) => r.status === "rejected").length;
    return { total: requests.length, pending, approved, rejected };
  }, [requests]);

  const visible = useMemo(() => {
    let out = requests;
    if (filter !== "all") out = out.filter((r) => r.status === filter);
    if (search) {
      const q = search.toLowerCase();
      out = out.filter((r) => {
        const emp = r.employee || {};
        return (
          `${emp.name || ""} ${emp.employeeid || ""} ${r.employeeId || ""} ${r.type || ""} ${r.reason || ""} ${r.location || ""}`
            .toLowerCase()
            .includes(q)
        );
      });
    }
    return out;
  }, [requests, filter, search]);

  const act = async (id, action) => {
    setActingId(id);
    try {
      const url = `/api/attendance/manager-approvals/${id}/${action}`;
      await api.put(url, { managerRemark: remarks[id] || "" });
      toast.success(`Request ${action}d`);
      load();
    } catch (err) {
      toast.error(extractErrorMessage(err));
    } finally { setActingId(""); }
  };

  return (
    <Box p={3}>
      <PageHeader
        title="Attendance Approvals"
        subtitle="Approve or reject your team's outside check-in and check-out requests"
        icon={<GroupsOutlinedIcon />}
        actions={[
          <IconButton key="r" onClick={load} aria-label="Refresh"><RefreshIcon /></IconButton>,
        ]}
      />

      <Grid container spacing={2} mb={3}>
        <Grid item xs={6} md={3}><StatCard label="Total" value={stats.total} accent="#6C50BF" /></Grid>
        <Grid item xs={6} md={3}><StatCard label="Pending" value={stats.pending} accent="#F9BC06" /></Grid>
        <Grid item xs={6} md={3}><StatCard label="Approved" value={stats.approved} accent="#39C645" /></Grid>
        <Grid item xs={6} md={3}><StatCard label="Rejected" value={stats.rejected} accent="#FF6B6B" /></Grid>
      </Grid>

      <SectionCard>
        <Stack direction={{ xs: "column", sm: "row" }} spacing={1.5} mb={2}>
          <TextField
            select size="small" value={filter} onChange={(e) => setFilter(e.target.value)}
            sx={{ minWidth: 160 }}
          >
            <MenuItem value="all">All</MenuItem>
            {sortByLabel(["approved", "pending", "rejected"]).map((s) => (
              <MenuItem key={s} value={s} sx={{ textTransform: "capitalize" }}>{s}</MenuItem>
            ))}
          </TextField>
          <TextField
            size="small" placeholder="Search employee, reason, location…"
            value={search} onChange={(e) => setSearch(e.target.value)}
            InputProps={{ startAdornment: <InputAdornment position="start"><SearchIcon fontSize="small" /></InputAdornment> }}
            sx={{ flex: 1 }}
          />
        </Stack>

        {loading ? (
          <Box display="flex" justifyContent="center" py={6}><CircularProgress /></Box>
        ) : visible.length === 0 ? (
          <EmptyState title="Nothing to review" subtitle="No requests match the current filter." />
        ) : (
          <Stack spacing={1.5}>
            {visible.map((r) => {
              const isPending = r.status === "pending";
              const emp = r.employee || {};
              const empName = emp.name || r.employeeId || "Unknown";
              const empId = emp.employeeid || r.employeeId || "—";
              const isCheckin = r.type === "checkin";

              return (
                <Box key={r._id} sx={{ p: 2, border: "1px solid", borderColor: "divider", borderRadius: 3 }}>
                  <Grid container spacing={2} alignItems="flex-start">
                    <Grid item xs={12} md={4}>
                      <Stack direction="row" spacing={1.5} alignItems="center">
                        <Avatar sx={{ width: 44, height: 44, bgcolor: "primary.main" }}>
                          {empName.slice(0, 1).toUpperCase()}
                        </Avatar>
                        <Box>
                          <Typography sx={{ fontSize: 15, fontWeight: 700 }}>{empName}</Typography>
                          <Typography sx={{ fontSize: 12, color: "text.secondary", fontFamily: "ui-monospace, Menlo, monospace" }}>
                            {empId}
                          </Typography>
                          <Chip
                            size="small"
                            icon={isCheckin ? <LoginOutlinedIcon /> : <LogoutOutlinedIcon />}
                            label={isCheckin ? "Check-in request" : "Check-out request"}
                            sx={{
                              mt: 0.75,
                              height: 22,
                              fontSize: 11,
                              fontWeight: 700,
                              bgcolor: isCheckin ? "#EBF0FF" : "#F5F3FF",
                              color: isCheckin ? "#4338CA" : "#6D28D9",
                            }}
                          />
                        </Box>
                      </Stack>
                    </Grid>

                    <Grid item xs={12} md={4}>
                      <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 700, textTransform: "uppercase" }}>
                        Requested at
                      </Typography>
                      <Typography sx={{ fontSize: 14, fontWeight: 600 }}>{fmtDateTime(r.requestedAt)}</Typography>
                      <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 1 }}>
                        Date: {fmtDate(r.requestedAt)} · Time: {fmtTime(r.requestedAt)}
                      </Typography>
                      <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 0.5 }}>
                        Request ID: {r._id}
                      </Typography>
                    </Grid>

                    <Grid item xs={12} md={4}>
                      <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 700, textTransform: "uppercase" }}>
                        Reason
                      </Typography>
                      <Typography sx={{ fontSize: 13, mb: 1 }}>{r.reason || "—"}</Typography>
                      <Stack direction="row" spacing={0.5} alignItems="flex-start" sx={{ mb: 1 }}>
                        <LocationOnOutlinedIcon sx={{ fontSize: 16, color: "text.secondary", mt: 0.2 }} />
                        <Typography sx={{ fontSize: 12, color: "text.secondary" }}>{r.location || "—"}</Typography>
                      </Stack>
                      {/* GPS verdict captured at request time. Inside-fence requests
                          auto-convert to office punches and never reach this queue,
                          so what shows here is either a distance flag or no-GPS. */}
                      {r.geo?.insideFence === false && (
                        <Chip
                          size="small"
                          color="warning"
                          sx={{ mb: 1, fontWeight: 700 }}
                          label={
                            r.geo.fenceName
                              ? `Outside geo-fence — ${r.geo.distanceMeters >= 1000 ? `${(r.geo.distanceMeters / 1000).toFixed(1)} km` : `${r.geo.distanceMeters} m`} from ${r.geo.fenceName}`
                              : "Outside geo-fence — no fences configured"
                          }
                        />
                      )}
                      {r.geo == null && (
                        <Chip size="small" variant="outlined" sx={{ mb: 1 }} label="GPS unavailable" />
                      )}
                      <StatusChip status={r.status} />
                      {r.reviewedAt && (
                        <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 0.5 }}>
                          Reviewed: {fmtDateTime(r.reviewedAt)}
                        </Typography>
                      )}
                    </Grid>

                    <Grid item xs={12}>
                      {isPending ? (
                        <Stack direction={{ xs: "column", sm: "row" }} spacing={1} alignItems={{ sm: "center" }}>
                          <TextField
                            size="small" fullWidth placeholder="Manager remark (optional)"
                            value={remarks[r._id] || ""}
                            onChange={(e) => setRemarks((rs) => ({ ...rs, [r._id]: e.target.value }))}
                          />
                          <Stack direction="row" spacing={1} sx={{ flexShrink: 0 }}>
                            <Button
                              size="small" variant="contained" color="success"
                              startIcon={<CheckCircleOutlineIcon />}
                              disabled={actingId === r._id}
                              onClick={() => act(r._id, "approve")}
                            >
                              Approve
                            </Button>
                            <Button
                              size="small" variant="outlined" color="error"
                              startIcon={<CancelOutlinedIcon />}
                              disabled={actingId === r._id}
                              onClick={() => act(r._id, "reject")}
                            >
                              Reject
                            </Button>
                          </Stack>
                        </Stack>
                      ) : (
                        r.managerRemark ? (
                          <Chip label={`Remark: ${r.managerRemark}`} size="small" variant="outlined" />
                        ) : null
                      )}
                    </Grid>
                  </Grid>
                </Box>
              );
            })}
          </Stack>
        )}
      </SectionCard>
    </Box>
  );
}
